import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { LoadScript, LoadScriptProps } from '@react-google-maps/api'
import {
  useStores,
  useConfig,
  useCountryData,
  useCookie,
  useEffectSkipFirst
} from 'msp-integrations'

import IconButton from '@molecules/IconButton/IconButton'
import Field from '@molecules/Field/Field'
import Headline from '@atoms/Headline/Headline'
import getStoreLocatorFields from './StoreLocator.fields'
import type { StoreLocatorProps } from './StoreLocator.types'
import { StoreLocatorResults } from './StoreLocator.partials'

const googleMapsLibraries: LoadScriptProps['libraries'] = ['places']

const StoreLocator = ({ data }: StoreLocatorProps) => {
  const { formatMessage } = useIntl()
  const {
    title,
    ssr: { googleMapsAPIKey },
    theme: { font }
  } = useConfig()
  const { isFunctionalAllowed, setCookieModal } = useCookie()
  const i18n = useCountryData()
  const countryCode = i18n.locale?.split('_')[1]
  const [stores, isLoading, getStores, clear] = useStores()
  const [searchTerm, setSearchTerm] = useState('')
  const [place, setPlace] = useState<{ name: string; id: string } | null>()
  const [suggestions, setSuggestions] = useState([])
  const [hasSuggestions, setHasSuggestions] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [googleAutoComplete, setGoogleAutoComplete] =
    useState<google.maps.places.AutocompleteService>()
  const form = useForm()

  const [cursor, setCursor] = useState(0)
  const fieldConfig = getStoreLocatorFields((e: any) => {
    setSearchTerm(e.target.value)
    getSuggestions(e.target.value)
  })

  useEffect(() => {
    window && window.addEventListener('keyup', downHandler)

    return () => {
      window && window.removeEventListener('keyup', downHandler)
    }
  })

  useEffect(() => {
    form.setValue('searchInput', place ? place.name : '')
    place && fetchStores()
  }, [place])

  useEffectSkipFirst(isFunctionalAllowed, () => {
    if (!isFunctionalAllowed) {
      location.reload()
    }
  })

  const downHandler = (e) => {
    if (hasSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setCursor((prevState) =>
          prevState < suggestions.length - 1 ? prevState + 1 : prevState
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        setPlace(suggestions[cursor])
      }
    }
  }
  const getSuggestions = (input: string) => {
    if (googleAutoComplete)
      googleAutoComplete.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: countryCode }
        },
        suggestionsResponseHandler
      )
    else setGoogleAutoComplete(new google.maps.places.AutocompleteService())
  }

  const suggestionsResponseHandler = (results: any) => {
    setHasSuggestions((results ?? []).length > 0)
    setSuggestions(
      (results ?? []).map((i) => ({ name: i.description, id: i.place_id }))
    )
  }

  const fetchStores = () => {
    setHasSuggestions(false)
    const service = new google.maps.places.PlacesService(
      document.createElement('div')
    )

    service.getDetails(
      {
        placeId: place!.id!,
        fields: ['geometry']
      },
      (location) => {
        if (location) {
          setShowResults(true)
          getStores({
            latitude: location.geometry?.location?.lat(),
            longitude: location.geometry?.location?.lng()
          })
        }
      }
    )
  }

  const clearSearch = () => {
    setSearchTerm('')
    setHasSuggestions(false)
    setPlace(null)
  }

  return (
    <div className='store-locator-container flex justify-center py-36 md:px-28'>
      <div className='flex min-h-[600px] w-11/12 flex-col md:w-9/12'>
        <Headline tag='h1' className='text-18 leading-16 text-white'>
          {formatMessage({
            defaultMessage: 'STORE FINDER',
            id: 'msp.storeLocator.title'
          })}
        </Headline>
        <Headline tag='h2' className='text-48 md:text-64 pb-8 text-white'>
          {data?.title}
        </Headline>
        <p className='text-16 md:text-18 pb-4 font-medium text-white'>
          {data?.subtitle}
        </p>
        <div
          className={`justify-top flex flex-col items-center ${font.regular}  bg-white pt-10 md:px-8`}
        >
          {isFunctionalAllowed ? (
            <LoadScript
              googleMapsApiKey={googleMapsAPIKey}
              libraries={googleMapsLibraries}
            >
              <Headline tag='h2' className='text-18 md:text-24 mb-4 md:mb-8'>
                {formatMessage(
                  {
                    defaultMessage: '{title} in store',
                    id: 'msp.storeLocator.store.title'
                  },
                  { title: title }
                )}
              </Headline>
              <p className='text-16 md:text-18 mx-8 w-4/5 max-w-[580px] pb-8 text-center font-medium'>
                {data?.description}
              </p>
              <div className='relative w-11/12 max-w-[380px] pb-8'>
                <Field
                  control={form.control}
                  config={fieldConfig.searchInput}
                  error={form.errors?.searchInput}
                  className={'mt-[-0.75rem]'}
                  additionalProps={{
                    layout: 'default',
                    inputClassName:
                      'tracking-0 text-14 h-10 w-full md:h-12 font-normal  px-[14px] text-black',
                    autoComplete: 'off'
                  }}
                />

                {searchTerm.length > 0 && (
                  <div
                    style={{ width: '20px' }}
                    className='absolute right-2 top-0 text-center'
                  >
                    <IconButton
                      iconName='Close'
                      height='20px'
                      width='20px'
                      color={'machine2-50'}
                      onClick={clearSearch}
                    />
                  </div>
                )}

                {hasSuggestions && searchTerm.length > 0 && (
                  <ul className='absolute right-0 top-9 z-[100] w-full bg-white'>
                    {suggestions
                      ? suggestions.map((item: any, index) => (
                          <li
                            key={item.id}
                            onMouseEnter={() => setCursor(index)}
                            className={`${
                              index === cursor ? 'bg-machine1-50' : ''
                            } hover:bg-machine1-50 border-veryLightGray cursor-pointer border ${
                              index !== 0 ? 'border-t-0' : ''
                            }   p-2`}
                            onClick={() => setPlace(item)}
                          >
                            <p className={'text-14'}>{item.name}</p>
                          </li>
                        ))
                      : ''}
                  </ul>
                )}
              </div>
            </LoadScript>
          ) : (
            <div className='flex flex-row flex-wrap items-center justify-center px-5 pb-10 text-center'>
              {formatMessage({
                id: 'msp.storeLocator.store.cookie_not_allowed_message',
                defaultMessage:
                  'In order to use the store finder, please adjust your cookie settings'
              })}
              <button
                onClick={() => setCookieModal(true, true)}
                className='mx-1 underline'
              >
                {formatMessage({
                  id: 'msp.storeLocator.store.open_cookie_modal_btn_text',
                  defaultMessage: 'here.'
                })}
              </button>
            </div>
          )}
          {showResults && (
            <ul
              className={
                'text-16 z-10 w-full bg-white px-2 py-5 md:max-h-[800px] md:overflow-y-scroll'
              }
            >
              {isLoading && (
                <li className='py-2'>
                  {formatMessage({
                    defaultMessage: 'Searching...',
                    id: 'msp.storeLocator.searching'
                  })}
                </li>
              )}
              {!isLoading && !stores?.data && (
                <li className='py-2'>
                  {formatMessage({
                    defaultMessage: 'No results found',
                    id: 'msp.storeLocator.noResults'
                  })}
                </li>
              )}
              {!isLoading && stores && (
                <StoreLocatorResults stores={stores.data} font={font} />
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default StoreLocator
